import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CourseNavItem } from './CourseNavItem'
import { TocRowMenu, tocMenuActions } from './TocRowMenu'
import { TocInlineRename } from './TocInlineRename'
import { TocDragHandle } from './TocDragHandle'
import { decodeHtml } from '@Utils'

export const CourseNavSubMenu = React.forwardRef(
  (
    {
      paddingLeft,
      isLocked,
      itemTitle,
      menuItems,
      coursenum,
      currentPage,
      depth,
      parentIdStr,
      itemId,
      tag,
      rawTitle,
      authorEditing,
      editingId,
      onAddItem,
      onStartRename,
      onRename,
      onCancelRename,
      onStartDelete,
      dropIndicator,
      moveBusy,
      revealId,
    },
    ref
  ) => {
    // Check if this submenu contains the current page
    const containsCurrent = containsCurrentPage(menuItems, currentPage)

    // Only set initial state based on whether it contains current page
    const [isOpen, setIsOpen] = useState(containsCurrent)
    const [shouldRender, setShouldRender] = useState(containsCurrent)
    const prevCurrentPageRef = useRef(currentPage)

    // Open the submenu when a freshly added child is in inline-rename mode,
    // or when a child was just dropped into it via drag-and-drop
    useEffect(() => {
      if (
        (editingId && containsCurrentPage(menuItems, editingId)) ||
        (revealId && containsCurrentPage(menuItems, revealId))
      ) {
        setShouldRender(true)
        const timer = setTimeout(() => setIsOpen(true), 10)
        return () => clearTimeout(timer)
      }
    }, [editingId, revealId, menuItems])

    // drag-and-drop registration (parentIdStr is this row's own id path)
    const dndData = {
      id: itemId,
      depth,
      tag,
      // mirrors _toc_child_tag_convention on the server: a chapter takes
      // nested chapters if it has ANY chapter child, sections otherwise -
      // keying off the first child alone disagrees on mixed-children chapters
      // and offers drops the server rejects with a 400
      childTag:
        !menuItems || menuItems.length === 0
          ? null
          : menuItems.some(child => child.tag === 'chapter')
            ? 'chapter'
            : 'section',
      pathIds: (parentIdStr || '').split('/').filter(Boolean),
      title: itemTitle,
    }
    const dndDisabled = !authorEditing || !!moveBusy || !itemId
    const {
      listeners,
      setNodeRef: setDragNodeRef,
      isDragging,
    } = useDraggable({ id: itemId || 'toc-submenu', data: dndData, disabled: dndDisabled })
    const { setNodeRef: setDropNodeRef } = useDroppable({
      id: itemId || 'toc-submenu',
      data: dndData,
      disabled: dndDisabled,
    })
    const setRowRefs = useCallback(
      el => {
        setDragNodeRef(el)
        setDropNodeRef(el)
        if (typeof ref === 'function') ref(el)
        else if (ref) ref.current = el
      },
      [setDragNodeRef, setDropNodeRef, ref]
    )

    const indicator =
      dropIndicator && dropIndicator.id === itemId ? dropIndicator.position : null

    // Update submenu state when currentPage changes
    useEffect(() => {
      // Only react to currentPage changes
      if (currentPage !== prevCurrentPageRef.current) {
        prevCurrentPageRef.current = currentPage
        const shouldBeOpen = containsCurrentPage(menuItems, currentPage)

        if (shouldBeOpen) {
          // Need to open this submenu
          setShouldRender(true)
          setTimeout(() => setIsOpen(true), 10)
        } else {
          // Need to close this submenu
          setIsOpen(false)
        }
      }
    }, [currentPage, menuItems])

    const handleKeyDown = useCallback(
      event => {
        const targetElement = event.target

        switch (event.key) {
          // groups enter and spacebar together
          case 'Enter':
          case ' ': // Space key
            event.preventDefault()
            event.stopPropagation()
            if (isOpen) {
              setIsOpen(false)
            } else {
              setShouldRender(true)
              // Small delay to ensure element is rendered before animation starts
              setTimeout(() => setIsOpen(true), 10)
            }
            targetElement.focus()
            break

          // groups left arrow and escape together
          case 'ArrowLeft':
          case 'Escape':
            if (isOpen) {
              event.preventDefault()
              event.stopPropagation()
              setIsOpen(false)
              targetElement.focus()
            }
            // If closed, let it bubble up to close the main menu
            break

          case 'ArrowRight':
            if (!isOpen) {
              event.preventDefault()
              event.stopPropagation()
              setShouldRender(true)
              setTimeout(() => setIsOpen(true), 10)
              targetElement.focus()
            }
            // If already open, let it bubble up for parent navigation
            break

          // Let ArrowUp and ArrowDown always bubble up to parent for menu navigation
          case 'ArrowUp':
          case 'ArrowDown':
            // Don't prevent default here - let parent handle navigation
            break

          default:
            // Let other keys bubble up
            break
        }
      },
      [isOpen]
    )

    const handleClick = useCallback(
      event => {
        // Only handle click events, not keyboard events
        if (event.detail !== 0) {
          // detail is 0 for keyboard events
          if (isOpen) {
            setIsOpen(false)
          } else {
            setShouldRender(true)
            setTimeout(() => setIsOpen(true), 10)
          }
        }
      },
      [isOpen]
    )

    // Handle unmounting after animation completes
    const handleTransitionEnd = useCallback(() => {
      if (!isOpen) {
        setShouldRender(false)
      }
    }, [isOpen])

    const isEditing = authorEditing && itemId && editingId === itemId

    return (
      <li
        className={`CourseNavMenuItem${
          indicator === 'before' ? ' toc-drop-before' : ''
        }${indicator === 'after' ? ' toc-drop-after' : ''}${
          isDragging ? ' toc-row-dragging' : ''
        }`}
        style={{ '--toc-drop-depth': depth }}
        role="none"
      >
        {isEditing ? (
          <div
            className="toc-inline-rename-row"
            style={{
              paddingLeft: depth > 1 ? `${paddingLeft * depth}px` : '8px',
            }}
          >
            <TocInlineRename
              initialValue={rawTitle}
              onSubmit={value => onRename(itemId, value)}
              onCancel={onCancelRename}
            />
          </div>
        ) : (
          <button
            className={`submenu-btn ${
              containsCurrent && !isOpen ? ' active' : ''
            }${indicator === 'inside' ? ' toc-drop-inside' : ''}`}
            style={{
              border: 'none',
              paddingLeft: depth > 1 ? `${paddingLeft * depth}px` : '0',
            }}
            ref={setRowRefs}
            role="menuitem"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            aria-label={`${decodeHtml(itemTitle)} - has submenu`}
          >
            <span className="nav-item-text">
              {isLocked && (
                <i
                  className="fa-regular fa-lock-alt fa-fw mr-2 icon_color_orange"
                  aria-hidden="true"
                />
              )}
              <i
                className="fa-light fa-chevron-right fa-fw chevron-icon"
                style={{
                  marginRight: paddingLeft,
                  fontSize: '10px',
                }}
                aria-hidden="true"
              />
              {decodeHtml(itemTitle)}
            </span>
          </button>
        )}
        {authorEditing && itemId && !isEditing && (
          <TocDragHandle listeners={listeners} title={decodeHtml(itemTitle)} />
        )}
        {authorEditing && itemId && !isEditing && (
          <TocRowMenu
            title={decodeHtml(itemTitle)}
            actions={tocMenuActions({
              tag,
              childTag: dndData.childTag,
              depth,
              itemId,
              title: decodeHtml(itemTitle),
              onAddItem,
              onStartRename,
              onStartDelete,
            })}
          />
        )}
        {shouldRender && (
          <div
            className={`submenu-container ${
              isOpen ? 'submenu-entered' : 'submenu-exiting'
            }`}
            onTransitionEnd={handleTransitionEnd}
          >
            <ul
              role="menu"
              className="ml-0 pl-0 mb-0"
              style={{ listStyleType: 'none' }}
            >
              {menuItems.map(item => {
                let idStr = !parentIdStr ? item.id : parentIdStr + '/' + item.id
                if (item.menuItems && item.menuItems.length > 0) {
                  return (
                    <CourseNavSubMenu
                      menuItems={item.menuItems}
                      isLocked={item.isLocked}
                      itemTitle={item.title}
                      paddingLeft={paddingLeft}
                      coursenum={coursenum}
                      currentPage={currentPage}
                      depth={depth + 1}
                      parentIdStr={idStr}
                      key={item.id}
                      itemId={item.id}
                      tag={item.tag}
                      rawTitle={item.rawTitle}
                      authorEditing={authorEditing}
                      editingId={editingId}
                      onAddItem={onAddItem}
                      onStartRename={onStartRename}
                      onRename={onRename}
                      onCancelRename={onCancelRename}
                      onStartDelete={onStartDelete}
                      dropIndicator={dropIndicator}
                      moveBusy={moveBusy}
                      revealId={revealId}
                    />
                  )
                }
                return (
                  <CourseNavItem
                    key={item.id}
                    paddingLeft={paddingLeft}
                    href={`/reader/web/${coursenum}/${idStr}/`}
                    idPath={idStr}
                    isLocked={item.isLocked}
                    itemTitle={item.title}
                    coursenum={coursenum}
                    currentPage={currentPage}
                    itemId={item.id}
                    depth={depth + 1}
                    tag={item.tag}
                    rawTitle={item.rawTitle}
                    authorEditing={authorEditing}
                    editingId={editingId}
                    onAddItem={onAddItem}
                    onStartRename={onStartRename}
                    onRename={onRename}
                    onCancelRename={onCancelRename}
                    onStartDelete={onStartDelete}
                    dropIndicator={dropIndicator}
                    moveBusy={moveBusy}
                  />
                )
              })}
            </ul>
          </div>
        )}
      </li>
    )
  }
)
CourseNavSubMenu.displayName = 'CourseNavSubMenu'

// Helper function to check if current page is within this submenu
function containsCurrentPage(menuItems, currentPage) {
  if (!menuItems || !currentPage) return false

  for (const item of menuItems) {
    // Check if this item is the current page
    if (item.id === currentPage) {
      return true
    }
    // Recursively check nested submenus
    if (item.menuItems && item.menuItems.length > 0) {
      if (containsCurrentPage(item.menuItems, currentPage)) {
        return true
      }
    }
  }
  return false
}
